import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenWrapper } from '../../components/Wrappers/ScreenWrapper';
import { useNavigate } from 'react-router';
import { EQuestsTabsValues, ERoutes } from '../../enums';
import { Box } from '@mui/material';
import { TMaterialStyles } from '../../theme';
import { getDailyQuests } from '../../queries';
import { useQuestsStore } from '../../stores';
import _ from 'lodash';
import { QuestItem } from '../../components/Quests/QuestItem';
import { useTranslation } from 'react-i18next';
import TypographyWithShadow from '../../components/Typography/TypographyWithShadow';
import { ScreenBanner } from '../../components/Common/ScreenBanner';
import { TabContext } from '@mui/lab';
import { CustomTabs, TTab } from '../../components/Tabs/Tabs';
import i18next from 'i18next';
import { CustomTabPanel } from '../../components/Tabs/TabPanel';

const QUESTS_TABS: TTab[] = [
  {
    label: i18next.t('quests.daily'),
    value: EQuestsTabsValues.daily,
  },
  {
    label: i18next.t('quests.general'),
    value: EQuestsTabsValues.general,
  },
];

const DailiesQuestsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tabValue, setTabValue] = useState<string>(EQuestsTabsValues.daily);

  const { quests } = useQuestsStore();

  const dailyQuests = useMemo(
    () => _.chain(quests).filter('daily_quest').sortBy('isCompleted').value(),
    [quests],
  );

  const generalQuests = useMemo(
    () =>
      _.chain(quests)
        .filter((quest) => !quest.daily_quest)
        .sortBy('isCompleted')
        .value(),
    [quests],
  );

  const onBack = useCallback(() => navigate(ERoutes.main), [navigate]);

  const onChangeTabValue = useCallback(
    (_e: any, value: string) => setTabValue(value),
    [],
  );

  const loadDailyQuests = useCallback(async () => {
    try {
      switch (tabValue) {
        case EQuestsTabsValues.daily:
          await getDailyQuests();
          break;
        case EQuestsTabsValues.general:
          await getDailyQuests();
          break;
      }
    } catch (error: any) {
      console.error('Error while [loadDailyQuests]', error);
    }
  }, [tabValue]);

  useEffect(() => {
    loadDailyQuests();
  }, [loadDailyQuests]);

  return (
    <ScreenWrapper onBack={onBack}>
      <Box sx={materialStyles.screen}>
        <ScreenBanner
          title={t('quests.quests')}
          illustrationName="QuestsBanner"
        />
        <TypographyWithShadow sx={materialStyles.description}>
          {t('quests.quest_screen_description')}
        </TypographyWithShadow>
        <TabContext value={tabValue}>
          <CustomTabs
            sx={materialStyles.tabsContainer}
            onChange={onChangeTabValue}
            tabs={QUESTS_TABS}
          />
          <CustomTabPanel value={EQuestsTabsValues.daily}>
            <Box sx={materialStyles.list}>
              {_.map(dailyQuests, (item) => (
                <QuestItem key={item.id} quest={item} />
              ))}
            </Box>
          </CustomTabPanel>
          <CustomTabPanel value={EQuestsTabsValues.general}>
            <Box sx={materialStyles.list}>
              {_.map(generalQuests, (item) => (
                <QuestItem key={item.id} quest={item} />
              ))}
            </Box>
          </CustomTabPanel>
        </TabContext>
      </Box>
    </ScreenWrapper>
  );
};

const materialStyles: TMaterialStyles = {
  screen: {
    pb: '24px',
    overflow: 'auto',
  },
  description: {
    mt: '22px',
    mb: '16px',
    textTransform: 'uppercase',
    fontSize: '15px',
    lineHeight: '100%',
    textAlign: 'center',
    fontWeight: '600',
    mx: '16px',
  },
  tabsContainer: {
    mx: '16px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    my: 2,
    gap: '13px',
    px: '16px',
  },
};

export default DailiesQuestsScreen;
